import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UmbrellaLogo } from "@/components/UmbrellaLogo";
import {
  FileText, ShieldCheck, Store, Inbox, Wallet, Check, ImagePlus, X,
  ArrowRight, CheckCircle2,
} from "lucide-react";

const STEPS = [
  [FileText, "Apply", "Fill in the short form below with your business details, trades and service areas. It takes a few minutes."],
  [ShieldCheck, "Pass vetting", "Our team verifies Black ownership, your licence and insurance, and runs a background check — the foundation of the trust clients place in us."],
  [Store, "Get listed", "Once approved, your business appears in Browse with Vetted, Bonded and Insured badges clients can trust."],
  [Inbox, "Receive job leads", "Clients post jobs in your area and trade. Send custom quotes and chat directly to win the work."],
  [Wallet, "Get paid through escrow", "Clients pay into secure escrow in CAD. Funds are released to you when the job is done right — no chasing invoices."],
];

const empty = {
  business_name: "", owner_full_name: "", email: "", phone: "",
  trades: [], service_areas: [], years_in_business: "",
  license_number: "", insurance_provider: "", insurance_expiry: "",
  wsib_number: "", website: "", description: "",
  logo_file_id: null, portfolio_file_ids: [], consent: false,
};

export default function ForBusinesses() {
  usePageMeta("For Businesses", "Apply to join Black Umbrella. Get vetted, get listed, receive job leads and get paid safely through escrow.");
  const [meta, setMeta] = useState({ trades: [], service_areas: [] });
  const [form, setForm] = useState(empty);
  const [logoPreview, setLogoPreview] = useState(null);
  const [portfolio, setPortfolio] = useState([]); // {file_id, preview}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const uploadOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/applications/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.file_id;
  };

  const onLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const id = await uploadOne(file);
      setForm((f) => ({ ...f, logo_file_id: id }));
      setLogoPreview(URL.createObjectURL(file));
    } catch { toast.error("Logo upload failed"); }
    finally { setUploading(false); }
  };

  const onPortfolio = async (files) => {
    if (!files?.length) return;
    const room = 5 - portfolio.length;
    if (room <= 0) { toast.error("Up to 5 portfolio photos"); return; }
    setUploading(true);
    try {
      const picked = Array.from(files).slice(0, room);
      const added = [];
      for (const file of picked) {
        const id = await uploadOne(file);
        added.push({ file_id: id, preview: URL.createObjectURL(file) });
      }
      setPortfolio((p) => [...p, ...added]);
      setForm((f) => ({ ...f, portfolio_file_ids: [...f.portfolio_file_ids, ...added.map((a) => a.file_id)] }));
    } catch { toast.error("Photo upload failed"); }
    finally { setUploading(false); }
  };

  const removePortfolio = (id) => {
    setPortfolio((p) => p.filter((x) => x.file_id !== id));
    setForm((f) => ({ ...f, portfolio_file_ids: f.portfolio_file_ids.filter((x) => x !== id) }));
  };

  const submit = async () => {
    const need = [
      ["business_name", form.business_name], ["owner_full_name", form.owner_full_name],
      ["email", form.email], ["phone", form.phone], ["years_in_business", form.years_in_business],
      ["insurance_provider", form.insurance_provider], ["insurance_expiry", form.insurance_expiry],
      ["description", form.description],
    ];
    if (need.some(([, v]) => !String(v).trim())) { toast.error("Please complete all required fields"); return; }
    if (!form.trades.length || !form.service_areas.length) { toast.error("Select at least one trade and one service area"); return; }
    if (!form.consent) { toast.error("Please consent to verification checks"); return; }
    setSaving(true);
    try {
      await api.post("/applications", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    } finally { setSaving(false); }
  };

  const lbl = "text-sm font-semibold text-slate-700";

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center" data-testid="application-confirmation">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 size={34} className="text-emerald-600" />
        </div>
        <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold text-slate-950">Application received</h1>
        <p className="mt-4 text-lg text-slate-600">
          Thank you for applying to join Black Umbrella. Our team will review your application —
          including Black ownership, licence, insurance and a background check — and reply by email.
        </p>
        <p className="mt-3 text-sm text-slate-500">Keep an eye on your inbox; you'll hear from us soon.</p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="for-businesses-page">
      {/* Hero */}
      <section className="relative brand-grid-bg">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-16 pb-10 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 bg-amber-500/10 px-3 py-1.5 rounded-full">
            <UmbrellaLogo size={14} /> For Black-owned businesses
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 leading-[1.05] text-balance">
            Grow your trade with <span className="text-amber-600">vetted</span> job leads.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Black Umbrella connects your Black-owned business with clients who want trustworthy, vetted trades —
            and pays you securely through escrow. Here's how joining works.
          </p>
          <Button data-testid="hero-apply-cta" onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-8 h-12 px-6 rounded-full bg-slate-950 hover:bg-amber-600 text-white text-base font-semibold">
            Apply to join <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
        <div className="space-y-4">
          {STEPS.map(([Icon, title, body], i) => (
            <div key={title} data-testid={`join-step-${i + 1}`}
              className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
              <div className="shrink-0 h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Icon size={22} className="text-amber-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">STEP {i + 1}</span>
                  <h3 className="font-heading text-lg font-bold text-slate-950">{title}</h3>
                </div>
                <p className="mt-1 text-slate-600">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="bg-slate-950 text-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center">Apply to join</h2>
          <p className="mt-3 text-center text-slate-300">Tell us about your business. Required fields are marked with *.</p>

          <div className="mt-10 rounded-2xl bg-white text-slate-900 p-6 sm:p-8 shadow-2xl space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={lbl}>Business name *</label>
                <Input data-testid="app-business-name" className="mt-2 h-11" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="e.g. Grencan Carpentry" />
              </div>
              <div>
                <label className={lbl}>Owner full name *</label>
                <Input data-testid="app-owner-name" className="mt-2 h-11" value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} placeholder="e.g. Grace Ncan" />
              </div>
              <div>
                <label className={lbl}>Email *</label>
                <Input data-testid="app-email" type="email" className="mt-2 h-11" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" />
              </div>
              <div>
                <label className={lbl}>Phone *</label>
                <Input data-testid="app-phone" className="mt-2 h-11" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(416) 555-0123" />
              </div>
            </div>

            <div>
              <label className={lbl}>Trades * <span className="font-normal text-slate-400">(select all that apply)</span></label>
              <div className="mt-2 flex flex-wrap gap-2">
                {meta.trades.map((t) => {
                  const on = form.trades.includes(t);
                  return (
                    <button type="button" key={t} data-testid={`app-trade-${t}`} onClick={() => toggle("trades", t)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-amber-400"}`}>
                      {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className={lbl}>Service areas * <span className="font-normal text-slate-400">(select all that apply)</span></label>
              <div className="mt-2 flex flex-wrap gap-2">
                {meta.service_areas.map((a) => {
                  const on = form.service_areas.includes(a);
                  return (
                    <button type="button" key={a} data-testid={`app-area-${a}`} onClick={() => toggle("service_areas", a)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-slate-950 border-slate-950 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-amber-400"}`}>
                      {on && <Check size={13} className="inline mr-1 -mt-0.5" />}{a}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={lbl}>Years in business *</label>
                <Input data-testid="app-years" type="number" min="0" className="mt-2 h-11" value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} placeholder="5" />
              </div>
              <div>
                <label className={lbl}>Licence number <span className="font-normal text-slate-400">(optional)</span></label>
                <Input data-testid="app-license" className="mt-2 h-11" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} placeholder="e.g. ESA/ECRA 7012345" />
              </div>
              <div>
                <label className={lbl}>Insurance provider *</label>
                <Input data-testid="app-insurance-provider" className="mt-2 h-11" value={form.insurance_provider} onChange={(e) => set("insurance_provider", e.target.value)} placeholder="e.g. Intact Insurance" />
              </div>
              <div>
                <label className={lbl}>Insurance expiry date *</label>
                <Input data-testid="app-insurance-expiry" type="date" className="mt-2 h-11" value={form.insurance_expiry} onChange={(e) => set("insurance_expiry", e.target.value)} />
              </div>
              <div>
                <label className={lbl}>WSIB number <span className="font-normal text-slate-400">(optional)</span></label>
                <Input data-testid="app-wsib" className="mt-2 h-11" value={form.wsib_number} onChange={(e) => set("wsib_number", e.target.value)} placeholder="e.g. 1234567" />
              </div>
              <div>
                <label className={lbl}>Website <span className="font-normal text-slate-400">(optional)</span></label>
                <Input data-testid="app-website" className="mt-2 h-11" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://yourbusiness.com" />
              </div>
            </div>

            <div>
              <label className={lbl}>Short description *</label>
              <Textarea data-testid="app-description" className="mt-2 min-h-[110px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell clients what you do, your experience and what sets you apart…" />
            </div>

            <div>
              <label className={lbl}>Business logo <span className="font-normal text-slate-400">(optional)</span></label>
              <div className="mt-2 flex items-center gap-3">
                {logoPreview ? (
                  <div className="relative">
                    <img src={logoPreview} alt="logo" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                    <button type="button" data-testid="app-logo-remove" onClick={() => { setLogoPreview(null); set("logo_file_id", null); }}
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"><X size={12} /></button>
                  </div>
                ) : (
                  <label className="cursor-pointer h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-amber-400">
                    <ImagePlus size={18} />
                    <span className="text-[10px] mt-0.5">Logo</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="app-logo-input" onChange={(e) => { onLogo(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>

            <div>
              <label className={lbl}>Portfolio photos <span className="font-normal text-slate-400">(optional, up to 5)</span></label>
              <div className="mt-2 flex flex-wrap gap-2">
                {portfolio.map((p) => (
                  <div key={p.file_id} className="relative">
                    <img src={p.preview} alt="portfolio" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                    <button type="button" data-testid={`app-portfolio-remove-${p.file_id}`} onClick={() => removePortfolio(p.file_id)}
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"><X size={12} /></button>
                  </div>
                ))}
                {portfolio.length < 5 && (
                  <label className="cursor-pointer h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-amber-400">
                    <ImagePlus size={18} />
                    <span className="text-[10px] mt-0.5">{uploading ? "…" : "Add"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="app-portfolio-input" onChange={(e) => { onPortfolio(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4 cursor-pointer">
              <Checkbox data-testid="app-consent" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5" />
              <span className="text-sm text-slate-600">
                I consent to Black Umbrella verifying my business, including Black ownership, licence, insurance,
                and a background check. *
              </span>
            </label>

            <Button data-testid="app-submit" disabled={saving || uploading} onClick={submit}
              className="w-full h-12 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold">
              {saving ? "Submitting…" : "Submit application"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
